import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Login() {
  const { user, login, switchUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [canReactivate, setCanReactivate] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Owner recovery: authenticate with any working (e.g. reception) login, then create/reset the owner.
  const [recOpen, setRecOpen] = useState(false);
  const [rec, setRec] = useState({ staffEmail: "", staffPassword: "", ownerEmail: "", ownerPassword: "", name: "Owner" });
  const [recError, setRecError] = useState("");
  const [recBusy, setRecBusy] = useState(false);
  const setR = (k, v) => setRec((s) => ({ ...s, [k]: v }));

  // Forgot password: text a code to the mobile on file, then set a new password.
  const [fpOpen, setFpOpen] = useState(false);
  const [fpStep, setFpStep] = useState(1);
  const [fp, setFp] = useState({ email: "", code: "", pw: "", pw2: "" });
  const [fpMsg, setFpMsg] = useState("");
  const [fpErr, setFpErr] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const setF = (k, v) => setFp((s) => ({ ...s, [k]: v }));
  const openForgot = () => { setFp({ email: email || "", code: "", pw: "", pw2: "" }); setFpStep(1); setFpMsg(""); setFpErr(""); setFpOpen(true); };
  const sendCode = async (e) => {
    e.preventDefault(); setFpErr(""); setFpMsg(""); setFpBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fp.email });
      if (data.success === false) { setFpErr(data.message || "No mobile on file for that account."); }
      else { setFpMsg(data.message || "We've texted you a code."); setFpStep(2); }
    } catch (err) { setFpErr(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setFpBusy(false); }
  };
  const doReset = async (e) => {
    e.preventDefault(); setFpErr("");
    if (fp.pw.length < 6) { setFpErr("New password must be at least 6 characters"); return; }
    if (fp.pw !== fp.pw2) { setFpErr("Passwords don't match"); return; }
    setFpBusy(true);
    try {
      await api.post("/auth/reset-password", { email: fp.email, code: fp.code.trim(), new_password: fp.pw });
      setFpOpen(false);
      await login(fp.email, fp.pw); navigate("/dashboard");
    } catch (err) { setFpErr(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setFpBusy(false); }
  };

  const recover = async (e) => {
    e.preventDefault();
    setRecError(""); setRecBusy(true);
    try {
      const { data: lg } = await api.post("/auth/login", { email: rec.staffEmail, password: rec.staffPassword });
      const { data: res } = await api.post("/business/recover-owner",
        { email: rec.ownerEmail, password: rec.ownerPassword, name: rec.name || "Owner" },
        { headers: { Authorization: `Bearer ${lg.token}` } });
      await switchUser(res.token);
      setRecOpen(false);
      navigate("/dashboard");
    } catch (err) { setRecError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setRecBusy(false); }
  };

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setCanReactivate(false); setLoading(true);
    try { await login(email, password); navigate("/dashboard"); }
    catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && detail.code === "account_deactivated") {
        setCanReactivate(true);
        setError(detail.msg || "This account is deactivated.");
      } else {
        setError(formatApiError(detail) || err.message);
      }
    }
    finally { setLoading(false); }
  };

  const doReactivate = async () => {
    setReactivating(true); setError("");
    try {
      const { data } = await api.post("/auth/reactivate", { email, password });
      await switchUser(data.token);
      navigate("/dashboard");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setReactivating(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center p-1 shadow-sm"><img src="/azordi-icon.png" alt="azordi" className="h-full w-full object-contain" data-testid="brand-icon" /></div>
          <span className="font-heading font-extrabold text-xl">Azordi</span>
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-4xl leading-tight">Run your beauty & aesthetics business, beautifully.</h1>
          <p className="opacity-80 mt-4 max-w-md">Bookings, clients, staff, reports and true-cost pricing — all in one place.</p>
        </div>
        <div className="opacity-60 text-sm">© Azordi</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="login-form">
          <div>
            <h2 className="font-heading font-bold text-2xl">Welcome back</h2>
            <p className="text-muted-foreground text-sm mt-1">Sign in to your business</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-lg" data-testid="login-email" required />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-lg" data-testid="login-password" required />
          </div>
          {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
          {canReactivate && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="reactivate-box">
              <p className="text-sm text-muted-foreground">This account was deactivated. All your records are safe — reactivate to restore access.</p>
              <Button type="button" onClick={doReactivate} disabled={reactivating} className="w-full h-11 rounded-lg" data-testid="reactivate-submit">
                {reactivating ? "Reactivating…" : "Reactivate account & sign in"}
              </Button>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <button type="button" onClick={openForgot} className="w-full text-center text-sm text-primary font-medium hover:underline" data-testid="forgot-password-link">Forgot password? Text me a code</button>
          <Dialog open={fpOpen} onOpenChange={setFpOpen}>
            <DialogContent className="sm:max-w-sm" data-testid="forgot-password-dialog">
              <DialogHeader><DialogTitle className="font-heading">Reset your password</DialogTitle></DialogHeader>
              {fpStep === 1 ? (
                <form onSubmit={sendCode} className="space-y-3">
                  <p className="text-sm text-muted-foreground">Enter your account email. We'll text a 6-digit code to the mobile number on file.</p>
                  <Input type="email" placeholder="Your email" value={fp.email} onChange={(e) => setF("email", e.target.value)} className="h-11 rounded-lg" data-testid="fp-email" required />
                  {fpErr && <div className="text-sm text-destructive" data-testid="fp-error">{fpErr}</div>}
                  <Button type="submit" disabled={fpBusy} className="w-full h-11 rounded-lg" data-testid="fp-send-code">{fpBusy ? "Sending…" : "Text me a code"}</Button>
                </form>
              ) : (
                <form onSubmit={doReset} className="space-y-3">
                  {fpMsg && <div className="text-sm text-emerald-600" data-testid="fp-message">{fpMsg}</div>}
                  <Input type="text" inputMode="numeric" placeholder="6-digit code" value={fp.code} onChange={(e) => setF("code", e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-11 rounded-lg tracking-widest text-center" data-testid="fp-code" required />
                  <Input type="password" placeholder="New password (min 6 chars)" value={fp.pw} onChange={(e) => setF("pw", e.target.value)} className="h-11 rounded-lg" data-testid="fp-new-password" required />
                  <Input type="password" placeholder="Confirm new password" value={fp.pw2} onChange={(e) => setF("pw2", e.target.value)} className="h-11 rounded-lg" data-testid="fp-confirm-password" required />
                  {fpErr && <div className="text-sm text-destructive" data-testid="fp-error">{fpErr}</div>}
                  <Button type="submit" disabled={fpBusy} className="w-full h-11 rounded-lg" data-testid="fp-reset-submit">{fpBusy ? "Resetting…" : "Reset password & sign in"}</Button>
                  <button type="button" onClick={() => { setFpStep(1); setFpErr(""); }} className="w-full text-center text-xs text-muted-foreground hover:text-primary" data-testid="fp-resend">Didn't get it? Send again</button>
                </form>
              )}
            </DialogContent>
          </Dialog>
          <p className="text-sm text-muted-foreground text-center">
            New here? <Link to="/register" className="text-primary font-medium" data-testid="link-register">Create a business</Link>
          </p>
          <Dialog open={recOpen} onOpenChange={setRecOpen}>
            <DialogTrigger asChild>
              <button type="button" className="w-full text-center text-xs text-muted-foreground hover:text-primary underline underline-offset-2" data-testid="recover-owner-link">Lost owner access? Recover it</button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="recover-owner-dialog">
              <DialogHeader><DialogTitle className="font-heading">Recover owner access</DialogTitle></DialogHeader>
              <form onSubmit={recover} className="space-y-4">
                <p className="text-sm text-muted-foreground">Sign in with any working login for your clinic (e.g. reception), then set the owner email & password. This only affects your own clinic.</p>
                <div className="rounded-xl border border-border p-3 space-y-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Your current working login</div>
                  <Input type="email" placeholder="Reception/staff email" value={rec.staffEmail} onChange={(e) => setR("staffEmail", e.target.value)} className="h-10 rounded-lg" data-testid="recover-staff-email" required />
                  <Input type="password" placeholder="Password" value={rec.staffPassword} onChange={(e) => setR("staffPassword", e.target.value)} className="h-10 rounded-lg" data-testid="recover-staff-password" required />
                </div>
                <div className="rounded-xl border border-border p-3 space-y-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">New owner login</div>
                  <Input type="text" placeholder="Owner name" value={rec.name} onChange={(e) => setR("name", e.target.value)} className="h-10 rounded-lg" data-testid="recover-owner-name" />
                  <Input type="email" placeholder="Owner email" value={rec.ownerEmail} onChange={(e) => setR("ownerEmail", e.target.value)} className="h-10 rounded-lg" data-testid="recover-owner-email" required />
                  <Input type="password" placeholder="Owner password (min 6 chars)" value={rec.ownerPassword} onChange={(e) => setR("ownerPassword", e.target.value)} className="h-10 rounded-lg" data-testid="recover-owner-password" minLength={6} required />
                </div>
                {recError && <div className="text-sm text-destructive" data-testid="recover-error">{recError}</div>}
                <Button type="submit" disabled={recBusy} className="w-full h-11 rounded-lg" data-testid="recover-submit">{recBusy ? "Recovering…" : "Recover owner & sign in"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </form>
      </div>
    </div>
  );
}
